'use client';

import { useEffect, useRef } from 'react';
import { isStandalonePwa, isTabletOrMobileDevice } from '@/lib/pwaDetect';

const CSS_HREF = '/pwa-add-to-homescreen/add-to-homescreen.min.css';
const JS_SRC = '/pwa-add-to-homescreen/add-to-homescreen_fr.min.js';
const SCRIPT_ID = 'pwa-add-to-homescreen-fr';

type AddToHomeScreenOptions = {
  appName: string;
  appNameDisplay?: string;
  appIconUrl: string;
  assetUrl: string;
  maxModalDisplayCount?: number;
  displayOptions?: { showMobile?: boolean; showDesktop?: boolean };
  allowClose?: boolean;
  showArrow?: boolean;
};

type AddToHomeScreenInstance = {
  show: (locale?: string) => unknown;
};

declare global {
  interface Window {
    AddToHomeScreen?: (options: AddToHomeScreenOptions) => AddToHomeScreenInstance;
    AddToHomeScreenInstance?: AddToHomeScreenInstance;
  }
}

function loadStylesheet(): void {
  if (document.querySelector(`link[href="${CSS_HREF}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = CSS_HREF;
  document.head.appendChild(link);
}

function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.AddToHomeScreen) {
      resolve();
      return;
    }
    if (document.getElementById(SCRIPT_ID)) {
      const wait = setInterval(() => {
        if (window.AddToHomeScreen) {
          clearInterval(wait);
          resolve();
        }
      }, 50);
      setTimeout(() => {
        clearInterval(wait);
        if (window.AddToHomeScreen) resolve();
        else reject(new Error('AddToHomeScreen timeout'));
      }, 8000);
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = JS_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load add-to-homescreen'));
    document.body.appendChild(script);
  });
}

/** Masque le bouton fermer de la librairie (modal non fermable sur mobile). */
function hideLibraryCloseButton(): void {
  const styleId = 'pwa-a2hs-no-close';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .adhs-container .adhs-close,
    .adhs-container [class*="close"],
    .adhs-modal-close {
      display: none !important;
      pointer-events: none !important;
    }
  `;
  document.head.appendChild(style);
}

export default function AddToHomeScreenPrompt() {
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    if (!isTabletOrMobileDevice() || isStandalonePwa()) return;

    startedRef.current = true;
    loadStylesheet();
    hideLibraryCloseButton();

    void loadScript()
      .then(() => {
        if (!window.AddToHomeScreen) return;

        window.AddToHomeScreenInstance = window.AddToHomeScreen({
          appName: 'DreamerLife',
          appNameDisplay: 'standalone',
          appIconUrl: '/apple-touch-icon.png',
          assetUrl: '/pwa-add-to-homescreen/assets/img/',
          maxModalDisplayCount: -1,
          displayOptions: { showMobile: true, showDesktop: false },
          allowClose: false,
          showArrow: true,
        });

        window.AddToHomeScreenInstance.show('fr');
      })
      .catch(() => {
        /* silencieux */
      });
  }, []);

  return null;
}
